import { useLocation, useNavigate } from '@tanstack/react-router';
import { useFlagsStatus } from '@unleash/proxy-client-react';
import { type FC, type PropsWithChildren, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { AuthState, useAuth, useIsRegistered } from '@furality/frontend-stack';
import ErrorDisplay, { PortalLoginErrorContent } from '@/components/ErrorDisplay.tsx';
import PageLoading from '@/components/loading/PageLoading.tsx';
import useFlag from '@/lib/hooks/useFlag.ts';
import useToasts from '@/lib/useToast.ts';

const RegisteredWrapper: FC<PropsWithChildren> = ({ children }) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { pushToast } = useToasts();
  const { t } = useTranslation();

  const { registered } = useIsRegistered();

  const { ready: translationReady } = useTranslation();

  const featuresReady = useFlagsStatus().flagsReady;
  const pending = useFlag('pending');

  // TODO: debouncing this is buggy, using flag instead
  const sentRegWarning = useRef(false);

  useEffect(() => {
    // handled by lockout
    if (featuresReady && pending) {
      return;
    }
    if (auth.state !== AuthState.Loading && translationReady) {
      if (auth.state === AuthState.NotLoggedIn) {
        auth.login();
      } else if (auth.state === AuthState.LoggedIn && !registered && pathname !== '/register') {
        if (!sentRegWarning.current) {
          sentRegWarning.current = true;
          void navigate({ to: '/register' });
          pushToast('error', t('shared.not_registered'));
        }
      }
    }
  }, [
    auth.state,
    translationReady,
    auth,
    registered,
    pathname,
    navigate,
    pushToast,
    t,
    featuresReady,
    pending,
  ]);

  if (auth.state === AuthState.Error) {
    return (
      <div className="flex-center h-100 flex-grow-1">
        <ErrorDisplay title={t('shared.login_error.title')} error={auth.error}>
          <PortalLoginErrorContent />
        </ErrorDisplay>
      </div>
    );
  }

  if (auth.state !== AuthState.LoggedIn || !registered) {
    return <PageLoading />;
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

export default RegisteredWrapper;
